import React, { useCallback, useState } from 'react';
import { PropTypes } from 'prop-types';
import Scrollable from '../Scrollable';
import ScrollIt from '../../utils/Scroll';
import { AngleLeftIcon, AngleRightIcon } from '../../styledhtml/Icon';
import {
  UnderlineTabRow,
  UnderlineTab,
  UnderlineTabDivider,
  SegmentTabTrack,
  SegmentTab,
  HomeTabRow,
  HomeTabButton,
  PillBarShell,
  PillTab,
  PillBarArrows,
} from './styled';

/**
 * Single-select tab bar: underline, segment, home (primary pills), or pill (compact chips + scroll).
 * Pass `value` + `onChange`, or set `active` / `onClick` per item (e.g. profile with two tab groups).
 */
function TabBar({
  variant,
  items,
  value,
  onChange,
  ariaLabel,
  rememberScrollPosition,
  equalWidth,
  rowStyle,
  segmentCompact,
  homeTabCompactPadding,
}) {
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const topicsRef = React.useRef(null);

  const onScroll = useCallback(() => {
    const el = topicsRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(Math.ceil(el.scrollLeft + el.offsetWidth) < el.scrollWidth);
  }, []);

  const scroll = useCallback((forward) => {
    const el = topicsRef.current;
    if (!el) return;
    const destination = Math.max(
      el.scrollLeft + (forward ? el.offsetWidth : -el.offsetWidth),
      0
    );
    ScrollIt(destination, el, undefined, undefined, undefined, true);
  }, []);

  const renderDivider = (key) => <UnderlineTabDivider key={key} aria-hidden />;

  const renderItem = (item, index) => {
    if (item.type === 'divider') {
      return renderDivider(`div-${index}`);
    }
    const active =
      item.active !== undefined ? item.active : value !== undefined && value === item.id;
    const handleClick =
      item.onClick || (() => onChange && item.id !== undefined && onChange(item.id));

    if (variant === 'underline') {
      return (
        <UnderlineTab
          key={item.id != null ? String(item.id) : `tab-${index}`}
          role="tab"
          aria-selected={active}
          $active={active}
          $alignStart={item.alignStart}
          onClick={handleClick}
        >
          {item.label}
        </UnderlineTab>
      );
    }
    if (variant === 'segment') {
      return (
        <SegmentTab
          key={item.id != null ? String(item.id) : `tab-${index}`}
          role="tab"
          aria-selected={active}
          $active={active}
          $compact={segmentCompact}
          onClick={handleClick}
        >
          {item.label}
        </SegmentTab>
      );
    }
    if (variant === 'home') {
      return (
        <HomeTabButton
          key={item.id != null ? String(item.id) : `tab-${index}`}
          role="tab"
          aria-selected={active}
          $active={active}
          $stretch={equalWidth}
          $compact={homeTabCompactPadding}
          onClick={handleClick}
        >
          {item.label}
        </HomeTabButton>
      );
    }
    if (variant === 'pill') {
      return (
        <PillTab
          key={item.id != null ? String(item.id) : `tab-${index}`}
          role="tab"
          aria-selected={active}
          $active={active}
          onClick={handleClick}
        >
          {item.label}
        </PillTab>
      );
    }
    return null;
  };

  if (variant === 'pill') {
    return (
      <PillBarShell onMouseEnter={onScroll} role="tablist" aria-label={ariaLabel || 'Topics'}>
        <Scrollable
          horizontal
          onScroll={onScroll}
          scrollRef={(e) => {
            topicsRef.current = e;
            onScroll();
          }}
          style={{ position: 'absolute', inset: 0 }}
          rememberScrollPosition={rememberScrollPosition || ''}
          hideScrollbar
        >
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            {items.map((item, index) => {
              if (item.type === 'divider') {
                return (
                  <div
                    key={`div-${index}`}
                    style={{ width: 1, alignSelf: 'stretch', background: 'rgba(0,0,0,0.12)', flexShrink: 0 }}
                  />
                );
              }
              return renderItem(item, index);
            })}
          </div>
        </Scrollable>
        <PillBarArrows>
          <div style={{ display: showLeft && 'flex' || 'none' }}>
            <div onClick={() => scroll(false)} role="presentation">
              <AngleLeftIcon style={{ width: 10, height: 10 }} />
            </div>
          </div>
          <div style={{ display: showRight && 'flex' || 'none' }}>
            <div onClick={() => scroll(true)} role="presentation">
              <AngleRightIcon style={{ width: 10, height: 10 }} />
            </div>
          </div>
        </PillBarArrows>
      </PillBarShell>
    );
  }

  const inner = items.map((item, index) => renderItem(item, index));

  if (variant === 'segment') {
    return (
      <SegmentTabTrack style={rowStyle} role="tablist" aria-label={ariaLabel}>
        {inner}
      </SegmentTabTrack>
    );
  }
  if (variant === 'home') {
    return (
      <HomeTabRow style={rowStyle} role="tablist" aria-label={ariaLabel}>
        {inner}
      </HomeTabRow>
    );
  }
  return (
    <UnderlineTabRow style={rowStyle} role="tablist" aria-label={ariaLabel}>
      {inner}
    </UnderlineTabRow>
  );
}

TabBar.propTypes = {
  variant: PropTypes.oneOf(['underline', 'segment', 'pill', 'home']).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({ type: PropTypes.oneOf(['divider']).isRequired }),
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.node,
        active: PropTypes.bool,
        onClick: PropTypes.func,
        alignStart: PropTypes.bool,
      }),
    ])
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  rememberScrollPosition: PropTypes.string,
  equalWidth: PropTypes.bool,
  rowStyle: PropTypes.object,
  segmentCompact: PropTypes.bool,
  homeTabCompactPadding: PropTypes.bool,
};

export default TabBar;
